// src/components/JobDetailPopup.js
// ---------------------------------------------------------------------------
// Shared pieces for the Job Details popups (Home, Home's calendar tab and the
// Calendar page), so they read the same everywhere:
//
//   <div className="modal jdp">            <- add "jdp" to the existing modal
//     <JobDetailHeader job={job} onClose={...} t={t} />
//     <div className="jdp-body">
//       <DetailFacts items={[{ label, value, wide }]} />
//       ...everything the popup already had...
//     </div>
//   </div>
//
// Styling lives in JobDetailPopup.css, all under .jdp / .jdp-*, so nothing
// leaks onto other pages and other pages' global .modal rules cannot squash
// it. Look borrowed from Chrysalis (navy header, soft blue cards), kept in
// Servicing's own colors for the buttons.
// ---------------------------------------------------------------------------

import React from "react";
import "./JobDetailPopup.css";

const statusTone = (status = "") => {
  const s = String(status).toLowerCase();
  if (s.includes("complete")) return "ok";
  if (s.includes("invoice") || s.includes("sent")) return "warn";
  if (s.includes("cancel") || s.includes("closed") || s.includes("denied")) return "danger";
  if (s.includes("need") || s.includes("hold") || s.includes("pending")) return "muted";
  return "active";
};

const cleanMarkers = (text = "") =>
  String(text || "")
    .replace(/\(\([a-z-]\)\)/gi, "")
    .replace(/\s+/g, " ")
    .trim();

const stripColon = (label = "") => String(label).replace(/:\s*$/, "");

/** Navy header: site, WO, status, service type, dates. */
export const JobDetailHeader = ({ job, onClose, t = (s) => s, eyebrow, children }) => {
  if (!job) return null;
  const tone = statusTone(job.status);
  const scope = cleanMarkers(job.scope);
  return (
    <div className="jdp-head">
      <div className="jdp-head__eyebrow">{eyebrow || t("Job Details")}</div>
      <div className="jdp-head__title">{job.siteName || t("No site name")}</div>
      {scope && <div className="jdp-head__scope">{scope}</div>}
      <div className="jdp-chips">
        {job.wo && <span className="jdp-chip jdp-chip--wo">WO {job.wo}</span>}
        {job.status && <span className={`jdp-chip jdp-chip--${tone}`}>{t(job.status)}</span>}
        {job.serviceType && <span className="jdp-chip">{t(job.serviceType)}</span>}
        {job.startDate && (
          <span className="jdp-chip">
            {String(job.startDate).slice(0, 10)}
            {job.endDate && String(job.endDate).slice(0, 10) !== String(job.startDate).slice(0, 10)
              ? ` → ${String(job.endDate).slice(0, 10)}`
              : ""}
          </span>
        )}
        {job.isProject && <span className="jdp-chip">{t("Project")}</span>}
        {children}
      </div>
      {onClose && (
        <button type="button" className="jdp-close" onClick={onClose} aria-label={t("Close")} title={t("Close")}>
          ×
        </button>
      )}
    </div>
  );
};

/**
 * Label / value tiles.
 *   items: [{ label, value, wide, hidden }]
 *   Empty values show a dash so the grid never jumps around.
 */
export const DetailFacts = ({ items = [] }) => (
  <dl className="jdp-facts">
    {items
      .filter((item) => item && !item.hidden)
      .map((item) => (
        <div key={item.key || item.label} className={`jdp-fact${item.wide ? " jdp-fact--wide" : ""}`}>
          <dt>{stripColon(item.label)}</dt>
          <dd>
            {item.value === 0 || (item.value !== null && item.value !== undefined && item.value !== "")
              ? item.value
              : <span className="jdp-dash">—</span>}
          </dd>
        </div>
      ))}
  </dl>
);

/** A titled card inside the popup body. */
export const DetailSection = ({ title, children, tone }) => (
  <section className={`jdp-section${tone ? ` jdp-section--${tone}` : ""}`}>
    {title && <h4 className="jdp-section__title">{title}</h4>}
    {children}
  </section>
);

export default JobDetailHeader;
